import React, { useEffect, useRef, useState } from "react";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { useLocation, useParams, useNavigate } from "react-router-dom";
import SidebarAdmin from "../Utils/SidebarAdmin";
import apiConfig from "../../config/apiConfig";
import { fetchForeignResource } from "../../apis/resources";
import { fetchEnum } from "../../apis/enum";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRoundsViewModel } from "../../viewModels/useRoundsViewModel";
import LogoutButton from "../LogoutButton";
import { apiFetch } from "../../apis/apiFetch";
import { ArrowLeft } from "lucide-react";
import RenderImage from "../QueryManager/RenderImage/RenderImage";
import DocumentPreview from "../Common/DocumentPreview";
import { AgGridReact } from "ag-grid-react";
import { ColDef } from "ag-grid-community";
import { Application } from "../Resource/ViewRounds";
import styles from "../Styles/CreateCertificate.module.css";
import { toast as toastUtil } from "../../utils/toast";

const PREV_STATUS_OPTIONS = [
  { label: "Offer Sent", value: "OFFER_SENT" },
  { label: "Accepted", value: "ACCEPTED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Withdrawn", value: "WITHDRAWN" },
  { label: "Expired", value: "EXPIRED" },
  { label: "Offer Cancelled", value: "OFFER_CANCELLED" },
  { label: "Offer Generated", value: "OFFER_GENERATED" },
  { label: "Offer Confirmed", value: "OFFER_CONFIRMED" },
];

const ALL_PREV_STATUS_VALUES = PREV_STATUS_OPTIONS.map((o) => o.value);

const RoundsEdit = () => {
  const { id }: any = useParams();
  const navigate = useNavigate();
  const baseUrl = apiConfig.getResourceUrl("Rounds");
  const apiUrl = `${apiConfig.getResourceUrl("Rounds")}?`;
  const metadataUrl = `${apiConfig.getResourceMetaDataUrl("Rounds")}?`;

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [editedRecord, setEditedRecord] = useState<any>({});
  const [resMetaData, setResMetaData] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastType, setToastType] = useState<"success" | "error">("success");
  const [toastMsg, setToastMsg] = useState("");

  const [gateCounts, setGateCounts] = useState<any>({
    cse_count: 0, ece_count: 0, ai_count: 0, cse_cutoff: null, ece_cutoff: null, ai_cutoff: null,
    btech_cse_count: 0, btech_ece_count: 0, btech_ai_count: 0, imtech_cse_count: 0, imtech_ece_count: 0,
    btech_cse_cutoff: null, btech_ece_cutoff: null, btech_ai_cutoff: null, imtech_cse_cutoff: null, imtech_ece_cutoff: null
  });

  // Previous-round selection state
  const [allocationMode, setAllocationMode] = useState<string>("");
  const [prevRoundId, setPrevRoundId] = useState<string>("");
  const [prevRoundMode, setPrevRoundMode] = useState<"INCLUDE" | "EXCLUDE">("INCLUDE");
  const [prevStatuses, setPrevStatuses] = useState<string[]>(["ACCEPTED", "REJECTED", "WITHDRAWN", "EXPIRED"]);

  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({});
  const regex = /^(g_|archived|extra_data)/;
  const fetchedResources = useRef(new Set<string>());
  const fetchedEnum = useRef(new Set<string>());
  const queryClient = useQueryClient();

  const [applications, setApplications] = useState<Application[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedApplications, setSelectedApplications] = useState<Record<string, string>>({});
  // Pagination State
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Removed redundant appId useParams and consolidated on 'id'
  const {
    fields,
    setFields,
    enums,
    setEnums,
    foreignKeyData,
    setForeignKeyData,
    dataToSave,
    setDataToSave,
    loadMetadata,
    save,
  } = useRoundsViewModel("admin@rasp.com", id);

  // ─── Fetch round by ID ────────────────────────────────────────────────────
  const fetchDataById = async (roundId: string) => {
    const args = encodeURIComponent(`id:${roundId}`);
    const url = `${baseUrl}?args=${args}&queryId=GET_BY_ID`;

    const response = await apiFetch(url);
    if (!response.ok) throw new Error("Network response was not ok");

    return response.json();
  };
  const useGetById = (roundId: string) =>
    useQuery({
      queryKey: ["getById", "Rounds", roundId],
      queryFn: () => fetchDataById(roundId),
      enabled: !!roundId,
    });

  const { data: fetchedDataById, isLoading: loadingEditComp } = useGetById(id);

  useEffect(() => {
    if (fetchedDataById && !loadingEditComp) {
      let resourceData: any = null;

      if (Array.isArray(fetchedDataById.resource) && fetchedDataById.resource.length > 0) {
        resourceData = fetchedDataById.resource[0];
      } else if (fetchedDataById.resource && typeof fetchedDataById.resource === "object" && !Array.isArray(fetchedDataById.resource)) {
        resourceData = fetchedDataById.resource;
      } else if (fetchedDataById?.id) {
        resourceData = fetchedDataById;
      }

      if (resourceData) {
        setEditedRecord((prevData: any) => {
          const newData = { ...prevData };
          Object.entries(resourceData).forEach(([key, val]) => {
            if (!regex.test(key)) {
              if ((key === "startdate" || key === "enddate" || key === "withdrawal_end_date" || key === "date") && val) {
                const d = new Date(val as string | number);
                if (!isNaN(d.getTime())) {
                  const year = d.getFullYear();
                  const month = String(d.getMonth() + 1).padStart(2, "0");
                  const day = String(d.getDate()).padStart(2, "0");
                  newData[key] = `${year}-${month}-${day}`;
                } else {
                  newData[key] = val;
                }
              } else {
                newData[key] = val;
              }
            }
          });
          return newData;
        });

        // Restore prev-round selection from stored fields
        if (resourceData.allocation_mode) {
          setAllocationMode(resourceData.allocation_mode);
        }
        if (resourceData.prev_round_id) {
          setPrevRoundId(resourceData.prev_round_id);
        }
        if (resourceData.prev_round_mode) {
          setPrevRoundMode(resourceData.prev_round_mode as "INCLUDE" | "EXCLUDE");
        }
        if (resourceData.prev_statuses) {
          setPrevStatuses(resourceData.prev_statuses.split(",").map((s: string) => s.trim()));
        }

        // ── Restore snapshot counts and cutoffs from the backend natively so the "Allocating X candidates"
        // display shows the frozen count immediately on page load, without needing
        // the admin to click "Generate Cutoffs" first.
        if (resourceData.cse_count !== undefined || resourceData.cse_cutoff !== undefined || resourceData.btech_cse_cutoff !== undefined || resourceData.btech_cse_count !== undefined) {
          setGateCounts((prev: any) => ({
            ...prev,
            cse_count: resourceData.cse_count ?? prev.cse_count,
            ece_count: resourceData.ece_count ?? prev.ece_count,
            ai_count: resourceData.ai_count ?? prev.ai_count,
            cse_cutoff: resourceData.cse_cutoff ?? prev.cse_cutoff,
            ece_cutoff: resourceData.ece_cutoff ?? prev.ece_cutoff,
            ai_cutoff: resourceData.ai_cutoff ?? prev.ai_cutoff,
            btech_cse_count: resourceData.btech_cse_count ?? prev.btech_cse_count,
            btech_ece_count: resourceData.btech_ece_count ?? prev.btech_ece_count,
            btech_ai_count: resourceData.btech_ai_count ?? prev.btech_ai_count,
            imtech_cse_count: resourceData.imtech_cse_count ?? prev.imtech_cse_count,
            imtech_ece_count: resourceData.imtech_ece_count ?? prev.imtech_ece_count,
            btech_cse_cutoff: resourceData.btech_cse_cutoff ?? prev.btech_cse_cutoff,
            btech_ece_cutoff: resourceData.btech_ece_cutoff ?? prev.btech_ece_cutoff,
            btech_ai_cutoff: resourceData.btech_ai_cutoff ?? prev.btech_ai_cutoff,
            imtech_cse_cutoff: resourceData.imtech_cse_cutoff ?? prev.imtech_cse_cutoff,
            imtech_ece_cutoff: resourceData.imtech_ece_cutoff ?? prev.imtech_ece_cutoff,
            extra_data: resourceData.extra_data ?? prev.extra_data,
            btech_cse_fresh: resourceData.btech_cse_fresh ?? prev.btech_cse_fresh,
            btech_cse_slide: resourceData.btech_cse_slide ?? prev.btech_cse_slide,
            btech_cse_retained: resourceData.btech_cse_retained ?? prev.btech_cse_retained,
            btech_ece_fresh: resourceData.btech_ece_fresh ?? prev.btech_ece_fresh,
            btech_ece_slide: resourceData.btech_ece_slide ?? prev.btech_ece_slide,
            btech_ece_retained: resourceData.btech_ece_retained ?? prev.btech_ece_retained,
            btech_ai_fresh: resourceData.btech_ai_fresh ?? prev.btech_ai_fresh,
            btech_ai_slide: resourceData.btech_ai_slide ?? prev.btech_ai_slide,
            btech_ai_retained: resourceData.btech_ai_retained ?? prev.btech_ai_retained,
            imtech_cse_fresh: resourceData.imtech_cse_fresh ?? prev.imtech_cse_fresh,
            imtech_cse_slide: resourceData.imtech_cse_slide ?? prev.imtech_cse_slide,
            imtech_cse_retained: resourceData.imtech_cse_retained ?? prev.imtech_cse_retained,
            imtech_ece_fresh: resourceData.imtech_ece_fresh ?? prev.imtech_ece_fresh,
            imtech_ece_slide: resourceData.imtech_ece_slide ?? prev.imtech_ece_slide,
            imtech_ece_retained: resourceData.imtech_ece_retained ?? prev.imtech_ece_retained,
            btech_cse_paid_slide: resourceData.btech_cse_paid_slide ?? prev.btech_cse_paid_slide,
            btech_ece_paid_slide: resourceData.btech_ece_paid_slide ?? prev.btech_ece_paid_slide,
            btech_ai_paid_slide: resourceData.btech_ai_paid_slide ?? prev.btech_ai_paid_slide,
            imtech_cse_paid_slide: resourceData.imtech_cse_paid_slide ?? prev.imtech_cse_paid_slide,
            imtech_ece_paid_slide: resourceData.imtech_ece_paid_slide ?? prev.imtech_ece_paid_slide,
          }));
        }
      }
    }
  }, [fetchedDataById, loadingEditComp]);


  // ─── Metadata ─────────────────────────────────────────────────────────────
  const { data: metaData, isLoading, error } = useQuery({
    queryKey: ["resMetaData", "roundsEdit"],
    queryFn: async () => {
      const res = await apiFetch(metadataUrl);
      if (!res.ok) throw new Error(`Failed to fetch metadata: ${res.statusText}`);
      const data = await res.json();

      setResMetaData(data);
      setFields(data[0].fieldValues);

      for (const field of data[0].fieldValues.filter((f: any) => f.foreign)) {
        if (!fetchedResources.current.has(field.foreign)) {
          fetchedResources.current.add(field.foreign);
          await fetchForeignData(field.foreign, field.name, field.foreign_field);
        }
      }
      for (const field of data[0].fieldValues.filter((f: any) => f.isEnum === true)) {
        if (!fetchedEnum.current.has(field.possible_value)) {
          fetchedEnum.current.add(field.possible_value);
          await fetchEnumData(field.possible_value);
        }
      }
      return data;
    },
  });

  // ─── Drives for dropdown ──────────────────────────────────────────────────
  const { data: driveOptions, isLoading: drivesLoading, error: drivesError } = useQuery({
    queryKey: ["driveOptions", "roundsEdit"],
    queryFn: async () => {
      const res = await apiFetch(`${apiConfig.getResourceUrl("drive")}?queryId=GET_ALL`);
      if (!res.ok) throw new Error(`Drive fetch failed: ${res.status}`);
      const body = await res.json();
      return body?.resource || [];
    },
  });

  const drives = React.useMemo(
    () => (Array.isArray(driveOptions) ? driveOptions.filter((d: any) => d.archived !== "Y") : []),
    [driveOptions]
  );

  // ─── All rounds for the same drive (for "prev round" dropdown) ────────────
  const { data: allRoundsData } = useQuery({
    queryKey: ["allRounds", "roundsEdit"],
    queryFn: async () => {
      const res = await apiFetch(`${apiConfig.getResourceUrl("rounds")}?queryId=GET_ALL`);
      if (!res.ok) return [];
      const body = await res.json();
      return body?.resource || [];
    },
  });

  // Rounds for the same drive, sorted oldest first, excluding the current round
  const prevRoundOptions = React.useMemo(() => {
    if (!Array.isArray(allRoundsData) || !editedRecord.drive_id) return [];
    return allRoundsData
      .filter((r: any) => r.drive_id === editedRecord.drive_id && r.id !== id && r.round_type?.toUpperCase() === 'OFFLINE')
      .sort((a: any, b: any) => {
        const parse = (v: any) => { const d = new Date(v); return isNaN(d.getTime()) ? Number(v) || 0 : d.getTime(); };
        return parse(a.date) - parse(b.date);
      });
  }, [allRoundsData, editedRecord.drive_id, id]);

  // ─── Generate cutoffs ─────────────────────────────────────────────────────
  const fetchCounts = async () => {
    const selectedDrive = drives.find((d: any) => d.id === editedRecord.drive_id);
    if (selectedDrive?.drivetype !== "Mtech" && selectedDrive?.drivetype !== "Btech") return;

    const argsArr: string[] = [];
    if (editedRecord.drive_id) argsArr.push(`drive_id:${editedRecord.drive_id}`);
    if (editedRecord.allocation_type) argsArr.push(`allocation_type:${editedRecord.allocation_type}`);

    if (editedRecord.cse_seats) argsArr.push(`cse_seats:${editedRecord.cse_seats}`);
    if (editedRecord.ece_seats) argsArr.push(`ece_seats:${editedRecord.ece_seats}`);
    if (editedRecord.ai_seats) argsArr.push(`ai_seats:${editedRecord.ai_seats}`);

    // B.Tech
    if (editedRecord.btech_cse_seats) argsArr.push(`btech_cse_seats:${editedRecord.btech_cse_seats}`);
    if (editedRecord.btech_ece_seats) argsArr.push(`btech_ece_seats:${editedRecord.btech_ece_seats}`);
    if (editedRecord.btech_ai_seats) argsArr.push(`btech_ai_seats:${editedRecord.btech_ai_seats}`);
    if (editedRecord.imtech_cse_seats) argsArr.push(`imtech_cse_seats:${editedRecord.imtech_cse_seats}`);
    if (editedRecord.imtech_ece_seats) argsArr.push(`imtech_ece_seats:${editedRecord.imtech_ece_seats}`);

    if (editedRecord.id) argsArr.push(`round_id:${editedRecord.id}`);
    if (editedRecord.cse_gate_min) argsArr.push(`cse_gate_min:${editedRecord.cse_gate_min}`);
    if (editedRecord.cse_gate_max) argsArr.push(`cse_gate_max:${editedRecord.cse_gate_max}`);
    if (editedRecord.ece_gate_min) argsArr.push(`ece_gate_min:${editedRecord.ece_gate_min}`);
    if (editedRecord.ece_gate_max) argsArr.push(`ece_gate_max:${editedRecord.ece_gate_max}`);
    if (editedRecord.ai_gate_min) argsArr.push(`ai_gate_min:${editedRecord.ai_gate_min}`);
    if (editedRecord.ai_gate_max) argsArr.push(`ai_gate_max:${editedRecord.ai_gate_max}`);
    if (prevRoundId && allocationMode !== "RECOVERY") {
      argsArr.push(`prev_round_id:${prevRoundId}`);
      argsArr.push(`prev_round_mode:${prevRoundMode}`);
      argsArr.push(`prev_statuses:${prevStatuses.join("|")}`);
    }

    if ((allocationMode === "RECOVERY" || allocationMode === "SLIDING") && prevRoundId) {
      argsArr.push(`allocation_mode:${allocationMode}`);
      argsArr.push(`parent_round_id:${prevRoundId}`);
    } else if (allocationMode === "SLIDING") {
      argsArr.push(`allocation_mode:SLIDING`);
    }

    // Always force recompute when explicitly clicking "Generate Cutoffs"
    argsArr.push("force_recompute:true");

    const params = new URLSearchParams({ queryId: "GET_GATE_COUNTS" });
    if (argsArr.length > 0) params.append("args", argsArr.join(","));

    try {
      const res = await apiFetch(`${apiConfig.getResourceUrl("applications")}?${params.toString()}`);
      if (res.ok) {
        const data = await res.json();
        if (data?.resource?.[0]) {
          const root = data.resource[0];
          setGateCounts(root);
          setEditedRecord((prev: any) => ({
            ...prev,
            cse_cutoff: root.cse_cutoff,
            ece_cutoff: root.ece_cutoff,
            ai_cutoff: root.ai_cutoff,
            cse_count: root.cse_count,
            ece_count: root.ece_count,
            ai_count: root.ai_count,
            // B.Tech
            btech_cse_cutoff: root.btech_cse_cutoff,
            btech_ece_cutoff: root.btech_ece_cutoff,
            btech_ai_cutoff: root.btech_ai_cutoff,
            imtech_cse_cutoff: root.imtech_cse_cutoff,
            imtech_ece_cutoff: root.imtech_ece_cutoff,
            btech_cse_count: root.btech_cse_count,
            btech_ece_count: root.btech_ece_count,
            btech_ai_count: root.btech_ai_count,
            imtech_cse_count: root.imtech_cse_count,
            imtech_ece_count: root.imtech_ece_count,
          }));
        }
      }
    } catch (err) { }
  };
  // Auto-fetch when editing an existing round
  useEffect(() => {
    if (editedRecord?.id && editedRecord?.drive_id && editedRecord?.freeze !== 'Y') {
      const hasSeats = editedRecord.cse_seats || editedRecord.ece_seats || editedRecord.ai_seats || editedRecord.btech_cse_seats || editedRecord.btech_ece_seats || editedRecord.btech_ai_seats || editedRecord.imtech_cse_seats || editedRecord.imtech_ece_seats;
      const hasCutoffs = editedRecord.cse_gate_min || editedRecord.ece_gate_min || editedRecord.ai_gate_min;

      if (hasSeats || hasCutoffs) {
        fetchCounts();
      }
    }
  }, [editedRecord?.id, drives]);


  // ─── Helpers ──────────────────────────────────────────────────────────────
  const fetchEnumData = async (enumName: string) => {
    try {
      const data = await fetchEnum(enumName);
      setEnums((prev: any) => ({ ...prev, [enumName]: data }));
    } catch (err) { }
  };

  const fetchForeignData = async (foreignResource: string, fieldName: string, foreignField: string) => {
    try {
      const data = await fetchForeignResource(foreignResource);
      setForeignKeyData((prev: any) => ({ ...prev, [foreignResource]: data }));
    } catch (err) { }
  };

  const handleEdit = (field: string, value: any) => {
    setEditedRecord((prevData: any) => ({ ...prevData, [field]: value }));
  };

  const togglePrevStatus = (status: string) => {
    setPrevStatuses((prev) =>
      prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
    );
  };

  const toggleAllPrevStatuses = () => {
    setPrevStatuses((prev) =>
      prev.length === ALL_PREV_STATUS_VALUES.length ? [] : [...ALL_PREV_STATUS_VALUES]
    );
  };

  const formatDateDDMMYYYY = (dateValue: any) => {
    if (!dateValue) return "No Date";
    const d = new Date(dateValue);
    if (isNaN(d.getTime())) return "No Date";
    return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
  };

  const base64EncodeFun = (str: string) => btoa(unescape(encodeURIComponent(str)));

  const showAlert = (type: "success" | "error", msg: string) => {
    setToastType(type); setToastMsg(msg); setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!editedRecord.round_type) return showAlert("error", "Round Type is required");
    if (!editedRecord.details) return showAlert("error", "Details are required");

    const selectedDrive = drives.find((d: any) => d.id === editedRecord.drive_id);
    if (selectedDrive?.drivetype === "Mtech") {
      const allocType = editedRecord.allocation_type || "SEAT_BASED";
      if (allocType === "SEAT_BASED") {
        const cseField = isOnline ? "mtech_online_cse_seats" : "cse_seats";
        const eceField = isOnline ? "mtech_online_ece_seats" : "ece_seats";
        const aiField = isOnline ? "mtech_online_aids_seats" : "ai_seats";
        const hasCse = editedRecord[cseField] !== undefined && editedRecord[cseField] !== "";
        const hasEce = editedRecord[eceField] !== undefined && editedRecord[eceField] !== "";
        const hasAi = editedRecord[aiField] !== undefined && editedRecord[aiField] !== "";
        if (!hasCse && !hasEce && !hasAi) {
          return showAlert("error", "Please provide at least one Available Seats limit for M.Tech.");
        }
        if (editedRecord.cse_seats && !editedRecord.cse_cutoff) {
          const msg = isOnline ? "Please enter at least one Cutoff before saving." : "Please click 'Generate Cutoffs' before saving.";
          return showAlert("error", msg);
        }
      } else {
        const hasCse = editedRecord.cse_gate_min !== undefined && editedRecord.cse_gate_min !== "";
        const hasEce = editedRecord.ece_gate_min !== undefined && editedRecord.ece_gate_min !== "";
        const hasAi = editedRecord.ai_gate_min !== undefined && editedRecord.ai_gate_min !== "";
        if (!hasCse && !hasEce && !hasAi) {
          return showAlert("error", "Please provide at least one Cutoff Range for M.Tech.");
        }
        if (editedRecord.cse_gate_min && gateCounts.cse_count == null) {
          return showAlert("error", "Please click 'Generate Counts' before saving.");
        }
      }
      executeUpdate();
    } else if (selectedDrive?.drivetype === "Btech" && !isOnline && !isManual) {
      const allocType = editedRecord.allocation_type || "SEAT_BASED";
      if (allocType === "SEAT_BASED") {
        const hasBtechCse = editedRecord.btech_cse_seats !== undefined && editedRecord.btech_cse_seats !== "";
        const hasBtechEce = editedRecord.btech_ece_seats !== undefined && editedRecord.btech_ece_seats !== "";
        const hasBtechAi = editedRecord.btech_ai_seats !== undefined && editedRecord.btech_ai_seats !== "";
        const hasImtechCse = editedRecord.imtech_cse_seats !== undefined && editedRecord.imtech_cse_seats !== "";
        const hasImtechEce = editedRecord.imtech_ece_seats !== undefined && editedRecord.imtech_ece_seats !== "";
        if (allocationMode !== "RECOVERY" && !hasBtechCse && !hasBtechEce && !hasBtechAi && !hasImtechCse && !hasImtechEce) {
          return showAlert("error", "Please provide at least one Available Seats limit for B.Tech/IM.Tech programs.");
        }
        const hasAnyCutoff = !!(editedRecord.btech_cse_cutoff || editedRecord.btech_ece_cutoff || editedRecord.btech_ai_cutoff || editedRecord.imtech_cse_cutoff || editedRecord.imtech_ece_cutoff);
        if (allocationMode === "RECOVERY" && !hasAnyCutoff) {
          return showAlert("error", "Please click 'Generate Cutoffs' before saving.");
        }
        if (allocationMode !== "RECOVERY" && (hasBtechCse || hasBtechEce || hasBtechAi || hasImtechCse || hasImtechEce) && !hasAnyCutoff) {
          return showAlert("error", "Please click 'Generate Cutoffs' before saving.");
        }
      }
      executeUpdate();
    } else if (selectedDrive?.drivetype === "Btech" && isOnline && !isManual) {
      const allocType = dataToSave.allocation_type || "SEAT_BASED";
      if (allocType === "SEAT_BASED") {
        const has_btech_online_cse = dataToSave.btech_online_cse_seats !== undefined && dataToSave.btech_online_cse_seats !== "";
        const has_btech_online_ece = dataToSave.btech_online_ece_seats !== undefined && dataToSave.btech_online_ece_seats !== "";
        const has_btech_online_aids = dataToSave.btech_online_aids_seats !== undefined && dataToSave.btech_online_aids_seats !== "";
        const has_imtech_online_cse = dataToSave.imtech_online_cse_seats !== undefined && dataToSave.imtech_online_cse_seats !== "";
        const has_imtech_online_ece = dataToSave.imtech_online_ece_seats !== undefined && dataToSave.imtech_online_ece_seats !== "";

        if (!isOnline && !has_btech_online_cse && !has_btech_online_ece && !has_btech_online_aids && !has_imtech_online_cse && !has_imtech_online_ece) {
          return showAlert("error", "Please provide at least one Available Seats limit for B.Tech programs.");
        }

        if (!editedRecord.btech_cse_cutoff && !editedRecord.btech_ece_cutoff && !editedRecord.btech_ai_cutoff && !editedRecord.imtech_cse_cutoff && !editedRecord.imtech_ece_cutoff) {
          return showAlert("error", "Please enter Cutoffs' before submitting");
        }


      }
      executeUpdate();
    } else {
      // if (selectedApplications === null || Object.keys(selectedApplications).length <= 0) {
      //   return showAlert("error", "Please select applications before submitting");
      // }
      executeUpdate();
    }

  };

  const getpref = (name: string) => {
    if (name === "B.Tech. (Computer Science and Engineering)") return "BTech_CSE";
    if (name === "B.Tech. (Electronics and Communication Engineering)") return "BTech_ECE";
    if (name === "B.Tech. (Artificial Intelligence & Data Science)") return "BTech_AI_DS";
    if (name === "Integrated M.Tech (Computer Science and Engineering)") return "I_MTech_CSE";
    if (name === "Integrated M.Tech. (Electronics and Communication Engineering)") return "I_MTech_ECE";
  }

  const executeUpdate = async () => {
    const payload: any = { ...editedRecord };
    // Removed allocation_locked = "Y" as snapshots handle integrity now

    // Sanitize numeric fields: convert "" to null to avoid NumberFormatException on backend
    const numericFields = [
      'cse_seats', 'ece_seats', 'ai_seats',
      'cse_cutoff', 'ece_cutoff', 'ai_cutoff',
      'cse_gate_min', 'cse_gate_max', 'ece_gate_min', 'ece_gate_max', 'ai_gate_min', 'ai_gate_max',
      'cse_count', 'ece_count', 'ai_count',
      'btech_cse_seats', 'btech_ece_seats', 'btech_ai_seats', 'imtech_cse_seats', 'imtech_ece_seats',
      'btech_cse_cutoff', 'btech_ece_cutoff', 'btech_ai_cutoff', 'imtech_cse_cutoff', 'imtech_ece_cutoff',
      'btech_cse_count', 'btech_ece_count', 'btech_ai_count', 'imtech_cse_count', 'imtech_ece_count'
    ];

    numericFields.forEach(field => {
      if (payload[field] === "") {
        payload[field] = null;
      }
    });

    // Clear fields of the inactive strategy
    if (payload.allocation_type === "CUTOFF_BASED") {
      payload.cse_seats = null;
      payload.ece_seats = null;
      payload.ai_seats = null;
      payload.cse_cutoff = null;
      payload.ece_cutoff = null;
      payload.ai_cutoff = null;
    } else {
      payload.cse_gate_min = null;
      payload.cse_gate_max = null;
      payload.ece_gate_min = null;
      payload.ece_gate_max = null;
      payload.ai_gate_min = null;
      payload.ai_gate_max = null;
    }

    // Store prev-round selection as direct first-class fields
    if (prevRoundId) {
      payload.prev_round_id = prevRoundId;
      payload.prev_round_mode = prevRoundMode;
      payload.prev_statuses = prevStatuses.join(",");
    } else {
      payload.prev_round_id = null;
      payload.prev_round_mode = null;
      payload.prev_statuses = null;
    }

    payload.allocation_mode = allocationMode || null;
    const extraData = {
      "applications": Object.entries(selectedApplications).map(
        ([appNo, preference]) => ({
          appNo,
          preference: getpref(preference),
        })
      )
    }
    console.log("extradata", extraData)
    payload.extra_data = extraData
    const params = new FormData();
    const fileKeys = Object.keys(payload).filter((k) => payload[k] instanceof File);
    if (fileKeys.length > 0) {
      const fileKey = fileKeys[0];
      params.append(fileKey, payload[fileKey]);
      payload[fileKey] = "";
      params.append("description", "Offer Letter Template");
      params.append("appId", "admission-iiitb");
      params.append("dmsRole", "admin");
      params.append("user_id", "admin@rasp.com");
      params.append("tags", "offer_letter");
    }

    params.append("resource", base64EncodeFun(JSON.stringify(payload)));
    params.append("action", "MODIFY");

    try {
      const response = await apiFetch(apiUrl, { method: "POST", body: params });
      if (response.ok) {
        queryClient.invalidateQueries({ queryKey: ["allRounds"] });
        queryClient.invalidateQueries({ queryKey: ["roundsData"] });
        queryClient.invalidateQueries({ queryKey: ["resourceData"] });
        queryClient.invalidateQueries({ queryKey: ["getById"] });
        showAlert("success", "Updated successfully!");
        setTimeout(() => navigate("/createround"), 1500);
      } else {
        showAlert("error", `Update failed: ${response.statusText}`);
      }
    } catch (error) {
      showAlert("error", "An error occurred while saving.");
    }
  };

  if (loadingEditComp || !editedRecord) {
    return <div className="text-center mt-5"><div className="spinner-border text-primary" /></div>;
  }

  const isMtech = drives.find((d: any) => d.id === editedRecord?.drive_id)?.drivetype === "Mtech";
  const isBtech = drives.find((d: any) => d.id === editedRecord?.drive_id)?.drivetype === "Btech";
  const isOnline = editedRecord?.round_type === "Online" || editedRecord?.round_type?.toUpperCase() === "ONLINE";
  const isManual = editedRecord?.round_type === "Manual";
  const isFrozen = isOnline && editedRecord?.freeze === 'Y';

  const handlePreferenceSelect = (appNo: string, preference: string) => {
    setSelectedApplications((prev) => ({
      ...prev,
      [appNo]: preference
      // BTech_CSE, BTech_ECE, BTech_AI_DS,IMTech_CSE,IMTech_ECE
    }));
  };

  const handleAppSelection = (appNo: string, checked: boolean) => {
    // Remove preference when app is deselected
    if (!checked) {
      setSelectedApplications((prev) => {
        const updated = { ...prev };
        delete updated[appNo];
        return updated;
      });
    }
  };

  const fetchApplications = async (driveId: string) => {
    setLoading(true);
    setApplications([]);
    setPageNo(1)
    setPageSize(20)
    setSelectedApplications({})
    console.log("ddddddddd", driveId)
    try {
      const url = `${apiConfig.getResourceUrl("applications")}?queryId=GET_APPLICATIONS_BY_DRIVE&args=${encodeURIComponent(`drive_id:${driveId}`)}`;
      const response = await apiFetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      let apps = [];
      if (Array.isArray(data)) apps = data;
      else if (data && Array.isArray(data.resource)) apps = data.resource;
      console.log("data", response)
      setApplications(apps as Application[]);
      if (apps.length === 0) {
        toastUtil.info("No eligible applications found for this round.");
      }
    } catch (err) {
      console.error("ViewRounds: Error fetching applications", err);
      toastUtil.error("Error fetching applications for this round.");
    } finally {
      setLoading(false);
    }
  };

  const columnDefs: ColDef[] = [
    {
      headerName: "",
      width: 70,
      cellRenderer: (params: any) => (
        <input
          type="checkbox"
          checked={!!selectedApplications[params.data.application_number]}
          onChange={(e) =>
            handleAppSelection(
              params.data.application_number,
              e.target.checked
            )
          }
        />
      ),
    },
    {
      headerName: "#",
      width: 80,
      valueGetter: (params: any) =>
        (pageNo - 1) * pageSize + params.node.rowIndex + 1,
    },
    {
      headerName: "Application No.",
      field: "application_number",
      valueFormatter: (params: any) => params.value || "—",
      minWidth: 170,
    },
    {
      headerName: "User Name",
      field: "fullname",
      valueFormatter: (params: any) => params.value || "—",
      minWidth: 200,
    },
    {
      headerName: "Pref 1",
      field: "pref1",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Pref 2",
      field: "pref2",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Pref 3",
      field: "pref3",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Pref 4",
      field: "pref4",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Pref 5",
      field: "pref5",
      minWidth: 180,
      cellRenderer: (params: any) => (
        <>
          <input
            type="radio"
            name={`pref-${params.data.application_number}`}
            checked={
              params.value
                ? selectedApplications[params.data.application_number] ===
                params.value
                : false
            }
            onChange={() =>
              handlePreferenceSelect(
                params.data.application_number,
                params.value || ""
              )
            }
          />{" "}
          {params.value || "NA"}
        </>
      ),
    },
    {
      headerName: "Offer Status",
      field: "offer_status",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <span className="fw-bold">{params.value || "—"}</span>
      ),
    },
    {
      headerName: "Offer Payment Status",
      field: "offered_payment_status",
      minWidth: 150,
      cellRenderer: (params: any) => (
        <span className="fw-bold">{params.value || "—"}</span>
      ),
    },
  ];
  console.log("editedRecord", editedRecord)
  return (
    <div className="dash-layout">
      <SidebarAdmin
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={() => setSidebarCollapsed((p: any) => !p)}
      />

      <div className={`dash-wrapper ${sidebarCollapsed ? "dash-wrapper-collapsed" : ""}`}>
        <header className="dash-header">
          <div className="d-flex align-items-center">
            <button
              className="btn btn-link p-0 me-2 text-white d-flex align-items-center"
              onClick={() => navigate("/createround")}
              title="Back to Create Rounds"
            >
              <ArrowLeft size={24} />
            </button>
            <h1 className="dash-page-title m-0">Edit Round</h1>
          </div>

          <div className="dash-user-profile">
            <div className="dash-profile-circle" onClick={() => setShowDropdown((p) => !p)}>
              <span>A</span>
            </div>
            {showDropdown && (
              <div className="dash-profile-dropdown">
                <LogoutButton />
              </div>
            )}
          </div>
        </header>

        <div className="dash-content-body">
          {!loadingEditComp && (
            <div className="container mt-4">
              <form>
                <div className="card shadow-sm border-0 mb-4">
                  <div className="card-body">
                    <div className="row g-3">

                      {/* Name */}
                      <div className="col-md-12">
                        <label className="fw-bold mb-1">Name <span className="text-danger">*</span></label>
                        <input
                          type="text"
                          className="form-control"
                          value={editedRecord["name"] || ""}
                          onChange={(e) => handleEdit("name", e.target.value)}
                          placeholder="Enter round name"
                        />
                      </div>


                      {/* Round Type */}
                      <div className="col-md-12">
                        <label className="fw-bold mb-1">Round Type <span className="text-danger">*</span></label>
                        <select
                          className="form-control"
                          value={editedRecord["round_type"] || ""}
                          onChange={(e) => handleEdit("round_type", e.target.value)}
                        >
                          <option value="">Select Round Type</option>
                          {(enums["RoundType"] || []).map((e: any, idx: number) => (
                            <option key={idx} value={e}>{e}</option>
                          ))}
                        </select>
                      </div>

                      {/* Round Number (For Online Rounds) */}
                      {editedRecord["round_type"] === "Online" && (
                        <div className={styles.formGroup}>
                          <label className={styles.formLabel}>Round Number</label>
                          <select
                            className={styles.formControl}
                            value={editedRecord["round_number"] || ""}
                            onChange={(e) => handleEdit("round_number", e.target.value ? Number(e.target.value) : null)}
                          >
                            <option value="">Select Round Number</option>
                            {[1, 2, 3, 4, 5].map((num) => (
                              <option key={num} value={num}>{num}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* Start Date */}
                      <div className="col-md-12">
                        <label className="fw-bold mb-1">Start Date <span className="text-danger">*</span></label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD-MM-YYYY"
                            value={editedRecord["startdate"] ? dayjs(editedRecord["startdate"]) : null}
                            onChange={(newValue) => {
                              handleEdit("startdate", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                            }}
                            slotProps={{
                              textField: {
                                placeholder: "dd-mm-yyyy",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    padding: '0px 12px',
                                    backgroundColor: '#fff',
                                    borderRadius: '0.375rem',
                                    "& fieldset": { borderColor: '#dee2e6', borderWidth: '1px' },
                                    "&:hover fieldset": { borderColor: '#dee2e6' },
                                    "&.Mui-focused fieldset": {
                                      borderColor: '#86b7fe',
                                      boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)',
                                      borderWidth: '1px'
                                    }
                                  },
                                  "& .MuiOutlinedInput-input": { padding: '0.375rem 0px', fontSize: '1rem', color: '#212529', fontFamily: 'inherit', height: 'auto' }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </div>

                      {/* End Date */}
                      <div className="col-md-12">
                        <label className="fw-bold mb-1">End Date <span className="text-danger">*</span></label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD-MM-YYYY"
                            value={editedRecord["enddate"] ? dayjs(editedRecord["enddate"]) : null}
                            onChange={(newValue) => {
                              handleEdit("enddate", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                            }}
                            slotProps={{
                              textField: {
                                placeholder: "dd-mm-yyyy",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    padding: '0px 12px',
                                    backgroundColor: '#fff',
                                    borderRadius: '0.375rem',
                                    "& fieldset": { borderColor: '#dee2e6', borderWidth: '1px' },
                                    "&:hover fieldset": { borderColor: '#dee2e6' },
                                    "&.Mui-focused fieldset": {
                                      borderColor: '#86b7fe',
                                      boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)',
                                      borderWidth: '1px'
                                    }
                                  },
                                  "& .MuiOutlinedInput-input": { padding: '0.375rem 0px', fontSize: '1rem', color: '#212529', fontFamily: 'inherit', height: 'auto' }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </div>

                      {/* Withdrawal End Date */}
                      <div className="col-md-12">
                        <label className="fw-bold mb-1">Withdrawal End Date</label>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DatePicker
                            format="DD-MM-YYYY"
                            value={editedRecord["withdrawal_end_date"] ? dayjs(editedRecord["withdrawal_end_date"]) : null}
                            onChange={(newValue) => {
                              handleEdit("withdrawal_end_date", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                            }}
                            slotProps={{
                              textField: {
                                placeholder: "dd-mm-yyyy",
                                fullWidth: true,
                                sx: {
                                  "& .MuiOutlinedInput-root": {
                                    padding: '0px 12px',
                                    backgroundColor: '#fff',
                                    borderRadius: '0.375rem',
                                    "& fieldset": { borderColor: '#dee2e6', borderWidth: '1px' },
                                    "&:hover fieldset": { borderColor: '#dee2e6' },
                                    "&.Mui-focused fieldset": {
                                      borderColor: '#86b7fe',
                                      boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)',
                                      borderWidth: '1px'
                                    }
                                  },
                                  "& .MuiOutlinedInput-input": { padding: '0.375rem 0px', fontSize: '1rem', color: '#212529', fontFamily: 'inherit', height: 'auto' }
                                }
                              }
                            }}
                          />
                        </LocalizationProvider>
                      </div>

                      {/* Registration End Date */}
                      {isOnline && (
                        <div className="col-md-12">
                          <label className="fw-bold mb-1">Registration End Date <span className="text-danger">*</span></label>
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              format="DD-MM-YYYY"
                              value={editedRecord["registration_end_date"] ? dayjs(editedRecord["registration_end_date"]) : null}
                              onChange={(newValue) => {
                                handleEdit("registration_end_date", newValue && newValue.isValid() ? newValue.format('YYYY-MM-DD') : "");
                              }}
                              slotProps={{
                                textField: {
                                  placeholder: "dd-mm-yyyy",
                                  fullWidth: true,
                                  sx: {
                                    "& .MuiOutlinedInput-root": {
                                      padding: '0px 12px',
                                      backgroundColor: '#fff',
                                      borderRadius: '0.375rem',
                                      "& fieldset": { borderColor: '#dee2e6', borderWidth: '1px' },
                                      "&:hover fieldset": { borderColor: '#dee2e6' },
                                      "&.Mui-focused fieldset": {
                                        borderColor: '#86b7fe',
                                        boxShadow: '0 0 0 0.25rem rgba(13, 110, 253, 0.25)',
                                        borderWidth: '1px'
                                      }
                                    },
                                    "& .MuiOutlinedInput-input": { padding: '0.375rem 0px', fontSize: '1rem', color: '#212529', fontFamily: 'inherit', height: 'auto' }
                                  }
                                }
                              }}
                            />
                          </LocalizationProvider>
                        </div>
                      )}
                      <div className="col-md-12">
                        <label className="fw-bold mb-1">Drive <span className="text-danger">*</span></label>
                        <select
                          className="form-control"
                          value={editedRecord["drive_id"] || ""}
                          onChange={(e) => handleEdit("drive_id", e.target.value)}
                        >
                          <option value="" disabled>
                            {drivesLoading ? "Loading drives..." : "Select a drive"}
                          </option>
                          {drives.map((d: any) => (
                            <option key={d.id} value={d.id}>{d.name ?? d.id}</option>
                          ))}
                        </select>
                        {drivesError && <div className="text-danger small mt-1">Failed to load drives</div>}
                      </div>

                      {/* Details */}
                      <div className="col-md-12">
                        <label className="fw-bold mb-1">Details <span className="text-danger">*</span></label>
                        <textarea
                          className="form-control"
                          rows={3}
                          value={editedRecord["details"] || ""}
                          onChange={(e) => handleEdit("details", e.target.value)}
                          placeholder="Enter details..."
                        />
                      </div>

                      {/* Template Name */}
                      <div className="col-md-12">
                        <label className="fw-bold mb-1">Template Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={editedRecord["offer_letter_template_name"] || ""}
                          onChange={(e) => handleEdit("offer_letter_template_name", e.target.value)}
                          placeholder="e.g. M.Tech Offer Letter R1"
                        />
                      </div>

                      {/* Round Fee */}
                      <div className="col-md-12">
                        <label className="fw-bold mb-1">Fee <span className="text-danger">*</span></label>
                        <input
                          type="number"
                          className="form-control"
                          value={editedRecord["fee"] || ""}
                          onChange={(e) => handleEdit("fee", e.target.value)}
                          placeholder="Enter round name"
                        />
                      </div>

                      {/* ── M.Tech seat allocation section ───────────────────── */}
                      {isMtech && (
                        <div className="col-12 mt-3 p-3 border rounded bg-light">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Allocation Strategy</h5>
                          </div>

                          {!isOnline && (
                            <div className="mb-4">
                              <div className="form-check form-check-inline">
                                <input className="form-check-input" type="radio" name="allocation_type" id="allocSeat" value="SEAT_BASED"
                                  checked={editedRecord.allocation_type !== "CUTOFF_BASED"}
                                  onChange={() => {
                                    handleEdit("allocation_type", "SEAT_BASED");
                                    handleEdit("cse_gate_min", ""); handleEdit("cse_gate_max", "");
                                    handleEdit("ece_gate_min", ""); handleEdit("ece_gate_max", "");
                                    handleEdit("ai_gate_min", ""); handleEdit("ai_gate_max", "");
                                  }} />
                                <label className="form-check-label" htmlFor="allocSeat">Seat Based Allocation</label>
                              </div>
                              <div className="form-check form-check-inline">
                                <input className="form-check-input" type="radio" name="allocation_type" id="allocCutoff" value="CUTOFF_BASED"
                                  checked={editedRecord.allocation_type === "CUTOFF_BASED"}
                                  onChange={() => {
                                    handleEdit("allocation_type", "CUTOFF_BASED");
                                    handleEdit("cse_seats", ""); handleEdit("ece_seats", ""); handleEdit("ai_seats", "");
                                  }} />
                                <label className="form-check-label" htmlFor="allocCutoff">Cutoff Range Based Allocation</label>
                              </div>
                            </div>
                          )}

                          {editedRecord.allocation_type !== "CUTOFF_BASED" ? (
                            <>
                              {/* Seat rows */}
                              {[
                                { label: "M.Tech CSE", field: isOnline ? "mtech_online_cse_seats" : "cse_seats", countKey: "cse_count", cutoffKey: "cse_cutoff" },
                                { label: "M.Tech ECE", field: isOnline ? "mtech_online_ece_seats" : "ece_seats", countKey: "ece_count", cutoffKey: "ece_cutoff" },
                                { label: "M.Tech AI&DS", field: isOnline ? "mtech_online_aids_seats" : "ai_seats", countKey: "ai_count", cutoffKey: "ai_cutoff" },
                              ].map(({ label, field, countKey, cutoffKey }) => (
                                <div className="row mb-3" key={field}>
                                  <div className="col-md-2 fw-bold d-flex align-items-center">{label}</div>
                                  <div className="col-md-4">
                                    <input
                                      type="number"
                                      className="form-control"
                                      placeholder="Available Seats"
                                      value={editedRecord[field] || ""}
                                      onChange={(e) => handleEdit(field, e.target.value)}
                                    />
                                  </div>
                                  <div className="col-md-6 d-flex align-items-center gap-2 text-primary">
                                    {isOnline ? (
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Cutoff"
                                        value={editedRecord[cutoffKey] || ""}
                                        disabled={isFrozen}
                                        onChange={(e) => handleEdit(cutoffKey, e.target.value)}
                                        style={{ maxWidth: "150px" }}
                                      />
                                    ) : (
                                      <>
                                        {gateCounts[cutoffKey] != null && (
                                          <span className="badge bg-success">Cutoff: {gateCounts[cutoffKey]}</span>
                                        )}
                                        {gateCounts[countKey] > 0 && (
                                          <span>Allocating {gateCounts[countKey]} candidates</span>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </>
                          ) : (
                            <>
                              <div className="row mb-3">
                                <div className="col-md-3 fw-bold d-flex align-items-center">M.Tech CSE <span className="badge bg-primary ms-2">{gateCounts.cse_count ?? 0} Applications</span></div>
                                <div className="col-md-3">
                                  <input type="number" className="form-control" placeholder="Min Score" value={editedRecord.cse_gate_min || ""} onChange={(e) => handleEdit("cse_gate_min", e.target.value)} disabled={isFrozen} />
                                </div>
                                <div className="col-md-3">
                                  <input type="number" className="form-control" placeholder="Max Score" value={editedRecord.cse_gate_max || ""} onChange={(e) => handleEdit("cse_gate_max", e.target.value)} disabled={isFrozen} />
                                </div>
                              </div>

                              <div className="row mb-3">
                                <div className="col-md-3 fw-bold d-flex align-items-center">M.Tech ECE <span className="badge bg-primary ms-2">{gateCounts.ece_count ?? 0} Applications</span></div>
                                <div className="col-md-3">
                                  <input type="number" className="form-control" placeholder="Min Score" value={editedRecord.ece_gate_min || ""} onChange={(e) => handleEdit("ece_gate_min", e.target.value)} disabled={isFrozen} />
                                </div>
                                <div className="col-md-3">
                                  <input type="number" className="form-control" placeholder="Max Score" value={editedRecord.ece_gate_max || ""} onChange={(e) => handleEdit("ece_gate_max", e.target.value)} disabled={isFrozen} />
                                </div>
                              </div>

                              <div className="row mb-3">
                                <div className="col-md-3 fw-bold d-flex align-items-center">M.Tech AI&DS <span className="badge bg-primary ms-2">{gateCounts.ai_count ?? 0} Applications</span></div>
                                <div className="col-md-3">
                                  <input type="number" className="form-control" placeholder="Min Score" value={editedRecord.ai_gate_min || ""} onChange={(e) => handleEdit("ai_gate_min", e.target.value)} disabled={isFrozen} />
                                </div>
                                <div className="col-md-3">
                                  <input type="number" className="form-control" placeholder="Max Score" value={editedRecord.ai_gate_max || ""} onChange={(e) => handleEdit("ai_gate_max", e.target.value)} disabled={isFrozen} />
                                </div>
                              </div>
                            </>
                          )}

                          {/* ── Previous round selection ─────────────────────── */}
                          {!isOnline && (
                            <>
                              <hr className="my-3" />
                              <div className="mb-2">
                                <label className="fw-semibold mb-1 d-block">
                                  Select candidates from previous round
                                  <span className="text-muted small ms-2">(leave blank to use all eligible candidates)</span>
                                </label>

                                {/* Mode + round selector */}
                            <div className="d-flex align-items-center gap-3 mb-2">
                              <select
                                className="form-select"
                                style={{ maxWidth: 160 }}
                                value={prevRoundMode}
                                onChange={(e) => setPrevRoundMode(e.target.value as "INCLUDE" | "EXCLUDE")}
                                disabled={prevRoundOptions.length === 0}
                              >
                                <option value="INCLUDE">Include</option>
                                <option value="EXCLUDE">Exclude</option>
                              </select>
                              <select
                                className="form-select"
                                style={{ maxWidth: 380 }}
                                value={prevRoundId}
                                onChange={(e) => setPrevRoundId(e.target.value)}
                                disabled={prevRoundOptions.length === 0}
                              >
                                <option value="">All eligible candidates (no filter)</option>
                                {prevRoundOptions.map((r: any) => (
                                  <option key={r.id} value={r.id}>
                                    {r.name || r.id} ({formatDateDDMMYYYY(r.startdate || r.date)})
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {prevRoundId && (
                            <div className="mb-3">
                              <label className="fw-semibold mb-1 d-block">
                                {prevRoundMode === "INCLUDE" ? "Include" : "Exclude"} candidates with offer status:
                              </label>
                              <div className="d-flex flex-wrap gap-3">
                                {/* All checkbox */}
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="edit-status-ALL"
                                    checked={prevStatuses.length === ALL_PREV_STATUS_VALUES.length}
                                    ref={(el) => {
                                      if (el) el.indeterminate = prevStatuses.length > 0 && prevStatuses.length < ALL_PREV_STATUS_VALUES.length;
                                    }}
                                    onChange={toggleAllPrevStatuses}
                                  />
                                  <label className="form-check-label fw-semibold" htmlFor="edit-status-ALL">All</label>
                                </div>
                                {PREV_STATUS_OPTIONS.map(({ label, value }) => (
                                  <div className="form-check" key={value}>
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`edit-status-${value}`}
                                      checked={prevStatuses.includes(value)}
                                      onChange={() => togglePrevStatus(value)}
                                    />
                                    <label className="form-check-label" htmlFor={`edit-status-${value}`}>
                                      {label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                              {prevStatuses.length === 0 && (
                                <div className="text-danger small mt-1">
                                  Select at least one status to filter candidates.
                                </div>
                              )}
                            </div>
                          )}
                          </>
                          )}

                          {!isOnline && (
                            <div className="row mt-3">
                              <div className="col-md-12 text-end">
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  disabled={isFrozen || (!!prevRoundId && prevStatuses.length === 0)}

                                  onClick={fetchCounts}
                                >
                                  Generate Cutoffs & Refresh
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                      {(isBtech && isOnline) && (
                        <>
                          <div className="col-12 mt-3 p-3 bg-light border rounded" style={{ gridColumn: "1 / -1" }}>
                            <h5 className="mb-3">Allocation Strategy</h5>

                            <div className="mb-4">
                              <div className="form-check form-check-inline">
                                <input className="form-check-input" type="radio" name="allocation_type" id="allocSeat" value="SEAT_BASED"
                                  checked={editedRecord.allocation_type !== "CUTOFF_BASED"}
                                  onChange={() => handleEdit("allocation_type", "SEAT_BASED")} />
                                <label className="form-check-label" htmlFor="allocSeat">Seat Based Allocation</label>
                              </div>
                            </div>

                            {editedRecord.allocation_type !== "CUTOFF_BASED" && (
                              <>
                                {/* Seat rows */}
                                {[
                                  { label: "B.Tech CSE", field: "btech_online_cse_seats" },
                                  { label: "B.Tech ECE", field: "btech_online_ece_seats" },
                                  { label: "B.Tech AI&DS", field: "btech_online_aids_seats" },
                                  { label: "Integrated M.Tech CSE", field: "imtech_online_cse_seats" },
                                  { label: "Integrated M.Tech ECE", field: "imtech_online_ece_seats" },
                                ].map(({ label, field }) => (
                                  <div className="row mb-3" key={field}>
                                    <div className="col-md-2 fw-bold d-flex align-items-center">{label}</div>
                                    <div className="col-md-4">
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="Available Seats"
                                        value={(editedRecord as any)[field] || ""}
                                        onChange={(e) => handleEdit(field, e.target.value)}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                          <div className="col-12 mt-3 p-3 bg-light border rounded" style={{ gridColumn: "1 / -1" }}>
                            <h5 className="mb-3">Cut Off</h5>

                            <div className="col-md-12">
                              <input
                                type="number"
                                className="form-control"
                                value={editedRecord["btech_cse_cutoff"] || ""}
                                disabled={isFrozen}
                                onChange={(e) =>
                                  setEditedRecord((prevData: any) => ({
                                    ...prevData, online_cutOff: e.target.value,
                                    btech_cse_cutoff: e.target.value,
                                    btech_ece_cutoff: e.target.value,
                                    btech_ai_cutoff: e.target.value,
                                    imtech_cse_cutoff: e.target.value,
                                    imtech_ece_cutoff: e.target.value
                                  }))
                                }
                                placeholder="Enter the CutOff for Online Round"
                              />
                            </div>
                          </div>
                        </>
                      )}

                      {/* ── B.Tech seat allocation section ───────────────────── */}
                      {isBtech && editedRecord.round_type?.toUpperCase() === "OFFLINE" && (
                        <div className="col-12 mt-3 p-3 border rounded bg-light">
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">Allocation Strategy</h5>
                            <div className="d-flex align-items-center gap-3">
                              <span className="fw-semibold small text-muted">Round Mode:</span>
                              <div className="form-check form-check-inline mb-0">
                                <input className="form-check-input" type="radio" id="editModeFresh" name="editAllocationMode"
                                  checked={allocationMode === ""}
                                  onChange={() => { setAllocationMode(""); setPrevRoundId(""); }} />
                                <label className="form-check-label" htmlFor="editModeFresh">Fresh Round</label>
                              </div>
                              <div className="form-check form-check-inline mb-0">
                                <input className="form-check-input" type="radio" id="editModeRecovery" name="editAllocationMode"
                                  checked={allocationMode === "RECOVERY"}
                                  onChange={() => setAllocationMode("RECOVERY")} />
                                <label className="form-check-label" htmlFor="editModeRecovery">Revised Allocation Round (1.x, 2.x etc)</label>
                              </div>
                              <div className="form-check form-check-inline mb-0">
                                <input className="form-check-input" type="radio" id="editModeSliding" name="editAllocationMode"
                                  checked={allocationMode === "SLIDING"}
                                  onChange={() => setAllocationMode("SLIDING")} />
                                <label className="form-check-label" htmlFor="editModeSliding">Sliding Round</label>
                              </div>
                            </div>
                          </div>

                          <div className="mb-4">
                            <div className="form-check form-check-inline">
                              <input className="form-check-input" type="radio" name="allocation_type" id="allocSeatBtech" value="SEAT_BASED"
                                checked={editedRecord.allocation_type !== "CUTOFF_BASED"}
                                onChange={() => handleEdit("allocation_type", "SEAT_BASED")} />
                              <label className="form-check-label" htmlFor="allocSeatBtech">Seat Based Allocation</label>
                            </div>
                          </div>

                          <>
                            {allocationMode === "SLIDING" && (
                              <div className="row mb-2 mt-4">
                                <div className="col-md-3"></div>
                                <div className="col-md-3 fw-bold text-muted" style={{ fontSize: "0.85em", textTransform: "uppercase" }}>Total Target Capacity</div>
                                <div className="col-md-6"></div>
                              </div>
                            )}
                            {/* Seat rows */}
                            {[
                              { label: "B.Tech CSE", field: "btech_cse_seats", countKey: "btech_cse_count", cutoffKey: "btech_cse_cutoff" },
                              { label: "B.Tech ECE", field: "btech_ece_seats", countKey: "btech_ece_count", cutoffKey: "btech_ece_cutoff" },
                              { label: "B.Tech AI&DS", field: "btech_ai_seats", countKey: "btech_ai_count", cutoffKey: "btech_ai_cutoff" },
                              { label: "IM.Tech CSE", field: "imtech_cse_seats", countKey: "imtech_cse_count", cutoffKey: "imtech_cse_cutoff" },
                              { label: "IM.Tech ECE", field: "imtech_ece_seats", countKey: "imtech_ece_count", cutoffKey: "imtech_ece_cutoff" },
                            ].map(({ label, field, countKey, cutoffKey }) => (
                              <div className="row mb-3" key={field}>
                                <div className="col-md-3 fw-bold d-flex align-items-center">{label}</div>
                                <div className="col-md-3">
                                  <input
                                    type="number"
                                    className="form-control"
                                    placeholder={allocationMode === "RECOVERY" ? "Auto-calculated" : allocationMode === "SLIDING" ? "Total Seats" : "Configured Seats"}
                                    value={editedRecord[field] || ""}
                                    onChange={(e) => handleEdit(field, e.target.value)}
                                    disabled={allocationMode === "RECOVERY"}
                                  />
                                </div>
                                <div className="col-md-6 d-flex align-items-center gap-2 text-primary">
                                  {gateCounts[cutoffKey] != null && (
                                    <span className="badge bg-success">Cutoff: {gateCounts[cutoffKey]}</span>
                                  )}
                                  {gateCounts[countKey] > 0 && (
                                    <span className="text-primary">
                                      Allocating {(gateCounts[countKey] ?? 0) - (gateCounts?.extra_data?.[countKey.replace("_count", "_retained")] ?? gateCounts?.[countKey.replace("_count", "_retained")] ?? 0)} candidates
                                      {allocationMode === "SLIDING" && (
                                        <span className="ms-1" style={{ fontSize: "0.85em", color: "#6c757d" }}>
                                          ({gateCounts?.extra_data?.[countKey.replace("_count", "_fresh")] ?? gateCounts?.[countKey.replace("_count", "_fresh")] ?? 0} FRESH / {gateCounts?.extra_data?.[countKey.replace("_count", "_paid_slide")] ?? gateCounts?.[countKey.replace("_count", "_paid_slide")] ?? 0} PAID SLIDE / {gateCounts?.extra_data?.[countKey.replace("_count", "_slide")] ?? gateCounts?.[countKey.replace("_count", "_slide")] ?? 0} SLIDE / {gateCounts?.extra_data?.[countKey.replace("_count", "_retained")] ?? gateCounts?.[countKey.replace("_count", "_retained")] ?? 0} RETAINED)
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </>

                          {/* ── Previous round selection ─────────────────────── */}
                          <hr className="my-3" />
                          <div className="mb-2">
                            <label className="fw-semibold mb-1 d-block">
                              Select candidates from previous round
                              <span className="text-muted small ms-2">(leave blank to use all eligible candidates)</span>
                            </label>

                            {/* Mode + round selector */}
                            {allocationMode === "" && (
                              <div className="d-flex align-items-center gap-3 mb-2">
                                <select
                                  className="form-select"
                                  style={{ maxWidth: 160 }}
                                  value={prevRoundMode}
                                  onChange={(e) => setPrevRoundMode(e.target.value as "INCLUDE" | "EXCLUDE")}
                                >
                                  <option value="INCLUDE">Include</option>
                                  <option value="EXCLUDE">Exclude</option>
                                </select>
                                <select
                                  className="form-select"
                                  style={{ maxWidth: 380 }}
                                  value={prevRoundId}
                                  onChange={(e) => setPrevRoundId(e.target.value)}
                                >
                                  <option value="">All eligible candidates (no filter)</option>
                                  {prevRoundOptions.map((r: any) => (
                                    <option key={r.id} value={r.id}>
                                      {r.name || r.id} ({formatDateDDMMYYYY(r.startdate || r.date)})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {(allocationMode === "RECOVERY" || allocationMode === "SLIDING") && (
                              <div className="d-flex align-items-center gap-3 mb-2">
                                <select
                                  className="form-select"
                                  style={{ maxWidth: 380 }}
                                  value={prevRoundId}
                                  onChange={(e) => setPrevRoundId(e.target.value)}
                                >
                                  <option value="">Select parent round…</option>
                                  {prevRoundOptions.map((r: any) => (
                                    <option key={r.id} value={r.id}>
                                      {r.name || r.id} ({formatDateDDMMYYYY(r.startdate || r.date)})
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>

                          {prevRoundId && allocationMode === "" && (
                            <div className="mb-3">
                              <label className="fw-semibold mb-1 d-block">
                                {prevRoundMode === "INCLUDE" ? "Include" : "Exclude"} candidates with offer status:
                              </label>
                              <div className="d-flex flex-wrap gap-3">
                                {/* All checkbox */}
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="edit-status-btech-ALL"
                                    checked={prevStatuses.length === ALL_PREV_STATUS_VALUES.length}
                                    ref={(el) => {
                                      if (el) el.indeterminate = prevStatuses.length > 0 && prevStatuses.length < ALL_PREV_STATUS_VALUES.length;
                                    }}
                                    onChange={toggleAllPrevStatuses}
                                  />
                                  <label className="form-check-label fw-semibold" htmlFor="edit-status-btech-ALL">All</label>
                                </div>
                                {PREV_STATUS_OPTIONS.map(({ label, value }) => (
                                  <div className="form-check" key={value}>
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      id={`edit-status-btech-${value}`}
                                      checked={prevStatuses.includes(value)}
                                      onChange={() => togglePrevStatus(value)}
                                    />
                                    <label className="form-check-label" htmlFor={`edit-status-btech-${value}`}>
                                      {label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                              {prevStatuses.length === 0 && (
                                <div className="text-danger small mt-1">
                                  Select at least one status to filter candidates.
                                </div>
                              )}
                            </div>
                          )}

                          <div className="row mt-3">
                            <div className="col-md-12 text-end">
                              <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={isFrozen || (allocationMode === "RECOVERY" || allocationMode === "SLIDING" ? !prevRoundId : (!!prevRoundId && prevStatuses.length === 0))}
                                onClick={fetchCounts}
                              >
                                Generate Cutoffs & Refresh
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Offer Letter Template */}
                      <div className="col-12 mt-4">
                        <label className="fw-bold mb-1">Offer Letter Template (.docx)</label>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="file"
                            className="form-control"
                            accept=".docx"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              handleEdit("offer_letter_template_doc_id", file ?? "");
                            }}
                          />
                          {typeof editedRecord.offer_letter_template_doc_id === "string" &&
                            editedRecord.offer_letter_template_doc_id && (
                              <div className="ms-2">
                                <DocumentPreview
                                  docId={editedRecord.offer_letter_template_doc_id}
                                  label="Download Current Template (.docx)"
                                />
                              </div>
                            )}
                        </div>
                      </div>

                      {/* Manual Round */}

                      {isManual && isBtech &&
                        <>
                          {editedRecord.drive_id &&
                            <button type="button" style={{ margin: 20 }} className={styles.primaryBtn} onClick={() => fetchApplications(editedRecord.drive_id)}>Get Applications</button>}

                          {loading ?
                            <div>Loading....</div> :
                            applications?.length > 0 ?
                              <div
                                className="ag-theme-alpine"
                                style={{ width: "100%", height: 600 }}
                              >
                                <AgGridReact
                                  rowData={applications}
                                  columnDefs={columnDefs}
                                  pagination={true}
                                  paginationPageSize={10}
                                  defaultColDef={{
                                    sortable: true,
                                    filter: true,
                                    resizable: true,
                                    flex: 1,
                                  }}
                                  suppressRowClickSelection
                                  rowSelection="multiple"
                                  getRowId={(params) => params.data.id}
                                  rowHeight={48}
                                />
                              </div> : <div> No application found</div>}
                        </>
                      }


                      {/* Save */}
                      <div className="col-12 mt-4">
                        <span 
                          title={editedRecord.round_type?.toUpperCase() === 'OFFLINE' ? "Editing this data will modify or corrupt the rounds data." : ""}
                          style={{ cursor: editedRecord.round_type?.toUpperCase() === 'OFFLINE' ? "not-allowed" : "pointer", display: "inline-block" }}
                        >
                          <button 
                            className="btn btn-primary px-4 py-2" 
                            onClick={handleUpdate}
                            disabled={editedRecord.round_type?.toUpperCase() === 'OFFLINE'}
                            style={{ pointerEvents: editedRecord.round_type?.toUpperCase() === 'OFFLINE' ? "none" : "auto" }}
                          >
                            Save Changes
                          </button>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </form>



              {showToast && (
                <div className="toast-container position-fixed top-0 end-0 p-3 mt-5" style={{ zIndex: 1550 }}>
                  <div className="toast show" role="alert">
                    <div className="toast-header">
                      <strong className="me-auto">{toastType === "success" ? "Success" : "Error"}</strong>
                      <button type="button" className="btn-close" onClick={() => setShowToast(false)} />
                    </div>
                    <div className={`toast-body text-center ${toastType === "success" ? "text-success" : "text-danger"}`}>
                      {toastMsg}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoundsEdit;